import React, { useEffect, useState } from 'react';
import { Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import { useAuth } from '../context/AuthContext';
import { signInWithOAuth, sendEmailSignInLink } from '../services/appwrite';
import {
  OAUTH_PROVIDERS,
  EMAIL_METHOD,
  clearLoginNotice,
  loginErrorMessage,
  providerLabel,
  readLoginNotice,
  takePendingLink,
} from '../lib/signInMethods';
import { toast } from 'sonner';
import BrandMark from '../components/BrandMark';
import EmailSignInForm from '../components/auth/EmailSignInForm';
import SignInMethodButton from '../components/auth/SignInMethodButton';

// shadcn UI components
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

/**
 * Sign in: each OAuth provider, and Email (a one-time link).
 *
 * Whatever happened on the way here (a failed sign-in, a refused provider)
 * arrives as a notice in storage, set by AuthCallback, never in the URL — so
 * the address bar is always plain /login and a refresh shows a fresh page.
 *
 * A provider refused because the email already has an account (see
 * lib/signInMethods) lands on 'link-sent': AuthCallback has already emailed a
 * sign-in link, and the provider is connected once it's used. When the
 * provider could not tell us the email, 'link-email' asks for it instead.
 */
const Login = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notice] = useState(readLoginNotice);

  const [loading, setLoading] = useState(false);
  // 'choose' | 'email' | 'link-sent' | 'link-email'
  const [view, setView] = useState(
    notice?.type === 'link-sent' || notice?.type === 'link-email' ? notice.type : 'choose'
  );
  const [linkEmail, setLinkEmail] = useState(notice?.email || '');
  const [sendingLink, setSendingLink] = useState(false);

  // Read once: clear the notice, and tidy an old-style /login?error=… link.
  useEffect(() => {
    clearLoginNotice();
    const legacyError = new URLSearchParams(location.search).get('error');
    if (notice?.type === 'error') {
      toast.error(loginErrorMessage(notice.code), { id: 'login-notice' });
    } else if (legacyError) {
      toast.error(loginErrorMessage(), { id: 'login-notice' });
    }
    if (location.search || location.hash) {
      navigate('/login', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleOAuthLogin = async (provider) => {
    try {
      setLoading(true);
      const { error } = await signInWithOAuth(provider);
      if (error) {
        console.error(`${provider} OAuth error:`, error);
        toast.error(`Failed to sign in with ${providerLabel(provider)}. Please try again.`);
        setLoading(false);
      }
      // On success the browser navigates away to the provider.
    } catch (error) {
      console.error(`${provider} login error:`, error);
      toast.error('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const sendLink = async (event) => {
    event.preventDefault();
    const email = linkEmail.trim();
    if (!email) return;
    try {
      setSendingLink(true);
      await sendEmailSignInLink(email);
      setLinkEmail(email.toLowerCase());
      setView('link-sent');
    } catch (err) {
      console.error('Could not send sign-in link:', err);
      toast.error('We could not send a link. Please check the address and try again.');
    } finally {
      setSendingLink(false);
    }
  };

  const backToChoose = () => {
    // Backing out also drops the queued connection, so a later ordinary
    // sign-in doesn't bounce off to the refused provider.
    takePendingLink();
    setView('choose');
  };

  const linkLabel = providerLabel(notice?.provider);
  const isLinkView = view === 'link-sent' || view === 'link-email';

  let body;
  if (view === 'link-sent') {
    body = (
      <div className="space-y-4 text-center">
        <p className="text-sm">
          We haven't seen you sign in with a {linkLabel} account before. We've sent a link
          to <span className="font-medium">{linkEmail}</span> to verify you.
        </p>
        <p className="text-sm text-muted-foreground">
          Once this is done, your {linkLabel} account will be linked to your PanelGear account.
        </p>
        <Button variant="ghost" className="w-full" onClick={backToChoose}>
          Back to sign in
        </Button>
      </div>
    );
  } else if (view === 'link-email') {
    body = (
      <form onSubmit={sendLink} className="space-y-4">
        <p className="text-sm text-center">
          We haven't seen you sign in with a {linkLabel} account before. Enter your email and
          we'll send you a link to verify you.
        </p>
        <div className="space-y-2">
          <Label htmlFor="link-email">Email</Label>
          <Input
            id="link-email"
            type="email"
            value={linkEmail}
            onChange={(e) => setLinkEmail(e.target.value)}
            placeholder="you@yourshop.com"
            autoComplete="email"
            autoFocus
            required
            disabled={sendingLink}
            className="h-12 text-base"
          />
        </div>
        <Button type="submit" className="w-full h-12 text-base" disabled={sendingLink || !linkEmail.trim()}>
          {sendingLink ? 'Sending…' : 'Send link'}
        </Button>
        <Button type="button" variant="ghost" className="w-full" onClick={backToChoose} disabled={sendingLink}>
          Back to sign in
        </Button>
      </form>
    );
  } else if (view === 'email') {
    body = <EmailSignInForm onBack={backToChoose} />;
  } else {
    body = (
      <div className="space-y-3">
        {OAUTH_PROVIDERS.map((provider) => (
          <SignInMethodButton
            key={provider.id}
            method={provider}
            busy={loading}
            busyLabel="Signing in..."
            onClick={() => handleOAuthLogin(provider.id)}
          />
        ))}
        <SignInMethodButton
          method={EMAIL_METHOD}
          disabled={loading}
          onClick={() => setView('email')}
        />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-background p-4">
      <ThemeToggle />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center">
            <BrandMark size="lg" />
          </div>
          {/* Not on the link screens: they already say what is happening,
              and a second line about signing in only made them wordier. */}
          {!isLinkView && <p className="text-muted-foreground">Sign in to manage your shop</p>}
        </CardHeader>

        <CardContent className="space-y-6">
          {body}

          {view === 'choose' && (
            <div className="text-center text-sm space-y-2">
              <p className="text-muted-foreground">
                New here?{' '}
                <Link to="/register" className="text-primary hover:underline">
                  Set up your shop
                </Link>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
